import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Info, Loader2, Plus, Search, Split, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { adminInputClass } from '@/components/admin/AdminTableStyles';
import CategoryPicker from '@/components/admin/accounting/CategoryPicker';
import {
  accountingError,
  centsToDollarInput,
  dollarsToCents,
  formatCents,
  splitBalance,
} from '@/lib/accounting';
import { getAccountingCustomers } from '@/lib/api';
import { cn } from '@/lib/utils';

function todayInput() {
  return new Date().toISOString().slice(0, 10);
}

let lineCounter = 0;
function emptySplit(overrides = {}) {
  lineCounter += 1;
  return { key: `line-${lineCounter}`, category_id: '', amount: '', description: '', tax_ids: [], ...overrides };
}

/**
 * Who the money came from.
 *
 * Students are customers already — there are thousands of them and no
 * conversion step — so this searches rather than pre-loading a `<select>` that
 * would be unusable at that size and slow at any size.
 */
function CustomerField({ value, valueLabel, onChange, disabled }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const wrapper = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (wrapper.current && !wrapper.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const needle = query.trim();
    let cancelled = false;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await getAccountingCustomers({ q: needle, page_size: 15 });
        if (!cancelled) setResults(data?.items || []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, open]);

  if (value) {
    return (
      <div className="mt-1 flex h-10 items-center justify-between gap-2 rounded-md border border-[rgba(6,43,26,0.16)] bg-[#F7F8FA] px-3 text-sm">
        <span className="min-w-0 truncate text-[#062B1A]">{valueLabel || value}</span>
        {!disabled && (
          <button
            type="button"
            onClick={() => onChange('', '')}
            aria-label="Remove the customer"
            className="shrink-0 text-[#062B1A]/40 hover:text-[#062B1A]"
          >
            <X className="size-3.5" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="relative mt-1" ref={wrapper}>
      <div
        className={cn(
          'flex h-10 items-center gap-2 rounded-md border bg-white px-3',
          'border-[rgba(6,43,26,0.16)] focus-within:ring-2 focus-within:ring-[#D4AF37]/50',
          disabled && 'opacity-60'
        )}
      >
        <Search className="size-3.5 shrink-0 text-[#062B1A]/35" />
        <input
          type="text"
          value={query}
          disabled={disabled}
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          placeholder="Search students and customers"
          aria-label="Search customers"
          className="w-full bg-transparent text-sm outline-none placeholder:text-[#062B1A]/35"
        />
        {searching && <Loader2 className="size-3.5 shrink-0 animate-spin text-[#062B1A]/35" />}
      </div>
      {open && (
        <div className="absolute left-0 right-0 z-50 mt-1 max-h-56 overflow-y-auto rounded-lg border border-[#062B1A]/12 bg-white py-1 shadow-xl">
          {results.length === 0 ? (
            <p className="px-3 py-4 text-center text-xs text-[#062B1A]/45">
              {searching ? 'Searching…' : 'No customer matches that.'}
            </p>
          ) : (
            results.map((customer) => (
              <button
                key={customer.ref}
                type="button"
                onClick={() => {
                  onChange(customer.ref, customer.name);
                  setOpen(false);
                }}
                className="flex w-full items-baseline justify-between gap-2 px-3 py-2 text-left hover:bg-[#F7F8FA]"
              >
                <span className="truncate text-sm text-[#062B1A]">{customer.name}</span>
                <span className="shrink-0 text-[10px] uppercase tracking-[0.08em] text-[#062B1A]/40">
                  {customer.source_label}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Add or edit one movement of money.
 *
 * The dialog has two modes and the difference is not cosmetic. A **manual**
 * transaction is entirely this module's to change. A **projected** one — a
 * tuition payment, a commission cheque, a bill payment — is a view of a record
 * another workflow owns, so its date, amount, description and parties render
 * as facts rather than fields, and only the bookkeeping around them (which
 * account it moved through, how it is categorised, a note) is editable. The
 * server enforces exactly the same line; this just stops a person typing into
 * something that was always going to be refused.
 */
export default function TransactionDialog({
  open,
  onOpenChange,
  transaction = null,
  direction = 'withdrawal',
  chart,
  taxes = [],
  payees = [],
  canCreateCategory = false,
  onCreateCategory,
  onSubmit,
}) {
  const [form, setForm] = useState(null);
  const [customerLabel, setCustomerLabel] = useState('');
  const [saving, setSaving] = useState(false);

  const locked = Boolean(transaction && !transaction.editable);
  const editing = Boolean(transaction);
  const accountsById = chart?.byId || {};
  const paymentAccounts = chart?.payment_accounts || [];

  useEffect(() => {
    if (!open) return;
    if (transaction) {
      const lines = (transaction.splits || []).map((split) =>
        emptySplit({
          category_id: split.category_id,
          amount: centsToDollarInput(split.amount_cents),
          description: split.description || '',
          tax_ids: split.tax_ids || [],
        })
      );
      setForm({
        date: transaction.date || todayInput(),
        description: transaction.description || '',
        account_id: transaction.account_id || '',
        direction: transaction.direction,
        amount: centsToDollarInput(transaction.amount_cents),
        splits: lines.length ? lines : [emptySplit()],
        splitMode: lines.length > 1,
        customer_ref: transaction.customer_ref || '',
        payee_ref: transaction.payee_ref || '',
        notes: transaction.notes || '',
      });
      setCustomerLabel(transaction.customer?.name || '');
      return;
    }
    const fallback =
      paymentAccounts.find((account) => account.is_default_payment_account)?.id
      || paymentAccounts[0]?.id
      || '';
    setForm({
      date: todayInput(),
      description: '',
      account_id: fallback,
      direction,
      amount: '',
      splits: [emptySplit()],
      splitMode: false,
      customer_ref: '',
      payee_ref: '',
      notes: '',
    });
    setCustomerLabel('');
    // paymentAccounts is rebuilt on every chart load; keying the reset on the
    // dialog opening rather than on its identity keeps this from resetting a
    // half-typed form under the person filling it in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, transaction, direction]);

  const amountCents = dollarsToCents(form?.amount);
  const balance = useMemo(
    () => splitBalance(amountCents, form?.splits || []),
    [amountCents, form?.splits]
  );

  const setField = (patch) => setForm((current) => ({ ...current, ...patch }));

  const setLine = (key, patch) =>
    setForm((current) => ({
      ...current,
      splits: current.splits.map((line) => (line.key === key ? { ...line, ...patch } : line)),
    }));

  /**
   * Outside split mode there is one line and it *is* the transaction, so the
   * amount field and the line's amount are the same number and are kept that
   * way rather than being two fields that can disagree.
   */
  const setAmount = (value) =>
    setForm((current) => ({
      ...current,
      amount: value,
      splits: current.splitMode
        ? current.splits
        : current.splits.map((line, index) => (index === 0 ? { ...line, amount: value } : line)),
    }));

  const enterSplitMode = () =>
    setForm((current) => ({
      ...current,
      splitMode: true,
      splits: [...current.splits, emptySplit()],
    }));

  const leaveSplitMode = () =>
    setForm((current) => ({
      ...current,
      splitMode: false,
      splits: [{ ...current.splits[0], amount: current.amount }],
    }));

  const addLine = () =>
    setForm((current) => ({ ...current, splits: [...current.splits, emptySplit()] }));

  const removeLine = (key) =>
    setForm((current) => {
      const remaining = current.splits.filter((line) => line.key !== key);
      if (remaining.length <= 1) {
        return {
          ...current,
          splitMode: false,
          splits: [{ ...(remaining[0] || emptySplit()), amount: current.amount }],
        };
      }
      return { ...current, splits: remaining };
    });

  const toggleTax = (key, taxId) =>
    setForm((current) => ({
      ...current,
      splits: current.splits.map((line) =>
        line.key === key
          ? {
              ...line,
              tax_ids: line.tax_ids.includes(taxId)
                ? line.tax_ids.filter((id) => id !== taxId)
                : [...line.tax_ids, taxId],
            }
          : line
      ),
    }));

  const createCategory = useCallback(
    async (payload) => {
      try {
        return await onCreateCategory?.(payload);
      } catch (err) {
        throw new Error(accountingError(err, 'That category could not be added.'));
      }
    },
    [onCreateCategory]
  );

  const submit = async () => {
    if (!locked) {
      if (!form.description.trim()) {
        toast.error('A transaction needs a description.');
        return;
      }
      if (amountCents <= 0) {
        toast.error('Enter an amount greater than zero.');
        return;
      }
    }
    if (!form.account_id) {
      toast.error('Choose the account this money moved through.');
      return;
    }
    if (form.splits.some((line) => !line.category_id)) {
      toast.error('Every line needs a category.');
      return;
    }
    if (!balance.balanced) {
      toast.error(
        `The lines come to ${formatCents(balance.allocatedCents)}, but the transaction is ${formatCents(amountCents)}.`
      );
      return;
    }

    const splits = form.splits.map((line) => ({
      category_id: line.category_id,
      amount: Number(dollarsToCents(line.amount)) / 100,
      description: line.description || '',
      tax_ids: line.tax_ids || [],
    }));

    const payload = locked
      ? { account_id: form.account_id, splits, notes: form.notes }
      : {
          date: form.date,
          description: form.description,
          account_id: form.account_id,
          direction: form.direction,
          amount: Number(amountCents) / 100,
          splits,
          customer_ref: form.customer_ref,
          payee_ref: form.payee_ref,
          notes: form.notes,
        };

    setSaving(true);
    try {
      await onSubmit(payload);
      onOpenChange(false);
    } catch (err) {
      toast.error(accountingError(err, 'The transaction could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  const title = editing
    ? locked
      ? 'Categorise this transaction'
      : 'Edit transaction'
    : direction === 'deposit'
      ? 'Add a deposit'
      : 'Add a withdrawal';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {locked
              ? transaction?.locked_reason
              : direction === 'deposit' || form?.direction === 'deposit'
                ? 'Money in. Payments the portal already recorded appear on their own — this is for money that arrived some other way.'
                : 'Money out. Commission cheques and bill payments appear on their own — this is for spending the portal did not already record.'}
          </DialogDescription>
        </DialogHeader>

        {form && (
          <div className="space-y-4">
            {locked && (
              <div className="flex gap-2 rounded-lg border border-[#D4AF37]/40 bg-[#D4AF37]/8 px-3 py-2.5 text-xs leading-relaxed text-[#7A5A05]">
                <Info className="mt-0.5 size-3.5 shrink-0" />
                <p>
                  The date, amount and description below belong to{' '}
                  {transaction?.source?.label?.toLowerCase() || 'another record'} and are shown as
                  they were recorded. You can still say which account it moved through, how it is
                  categorised, and add a note.
                </p>
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="txn-date">Date</Label>
                {locked ? (
                  <p className="mt-1 flex h-10 items-center rounded-md bg-[#F7F8FA] px-3 text-sm text-[#062B1A]">
                    {transaction.date || '—'}
                  </p>
                ) : (
                  <Input
                    id="txn-date"
                    type="date"
                    value={form.date}
                    onChange={(event) => setField({ date: event.target.value })}
                    className={cn('mt-1', adminInputClass)}
                  />
                )}
              </div>
              <div>
                <Label htmlFor="txn-account">Account</Label>
                <select
                  id="txn-account"
                  value={form.account_id}
                  onChange={(event) => setField({ account_id: event.target.value })}
                  className={cn(
                    'mt-1 h-10 w-full rounded-md border bg-white px-3 text-sm',
                    adminInputClass
                  )}
                >
                  <option value="">Choose an account…</option>
                  {paymentAccounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label htmlFor="txn-description">Description</Label>
              {locked ? (
                <p className="mt-1 flex min-h-10 items-center rounded-md bg-[#F7F8FA] px-3 py-2 text-sm text-[#062B1A]">
                  {transaction.description || '—'}
                </p>
              ) : (
                <Input
                  id="txn-description"
                  value={form.description}
                  onChange={(event) => setField({ description: event.target.value })}
                  placeholder="What this payment was for"
                  className={cn('mt-1', adminInputClass)}
                />
              )}
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="txn-amount">Amount</Label>
                {locked ? (
                  <p className="mt-1 flex h-10 items-center rounded-md bg-[#F7F8FA] px-3 text-sm font-semibold text-[#062B1A]">
                    {transaction.amount_display}
                  </p>
                ) : (
                  <Input
                    id="txn-amount"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={form.amount}
                    onChange={(event) => setAmount(event.target.value)}
                    placeholder="0.00"
                    className={cn('mt-1', adminInputClass)}
                  />
                )}
              </div>
              <div>
                <Label htmlFor="txn-direction">Type</Label>
                {locked || !editing ? (
                  <p className="mt-1 flex h-10 items-center rounded-md bg-[#F7F8FA] px-3 text-sm text-[#062B1A]">
                    {form.direction === 'deposit' ? 'Deposit — money in' : 'Withdrawal — money out'}
                  </p>
                ) : (
                  <select
                    id="txn-direction"
                    value={form.direction}
                    onChange={(event) => setField({ direction: event.target.value })}
                    className={cn(
                      'mt-1 h-10 w-full rounded-md border bg-white px-3 text-sm',
                      adminInputClass
                    )}
                  >
                    <option value="deposit">Deposit — money in</option>
                    <option value="withdrawal">Withdrawal — money out</option>
                  </select>
                )}
              </div>
            </div>

            {/* ── Categories ──────────────────────────────────────────── */}
            <div className="rounded-lg border border-[#062B1A]/10 bg-[#F7F8FA]/50 p-3">
              <div className="flex items-center justify-between pb-2">
                <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/45">
                  {form.splitMode ? 'Split across categories' : 'Category'}
                </p>
                {form.splitMode ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={leaveSplitMode}
                  >
                    Use one category
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={enterSplitMode}
                  >
                    <Split className="mr-1 size-3" />
                    Split this transaction
                  </Button>
                )}
              </div>

              <div className="space-y-2">
                {form.splits.map((line, index) => (
                  <div
                    key={line.key}
                    className={cn(
                      'rounded-md border border-[#062B1A]/8 bg-white p-2.5',
                      !form.splitMode && 'border-transparent bg-transparent p-0'
                    )}
                  >
                    <div
                      className={cn(
                        'grid items-start gap-2',
                        form.splitMode
                          ? 'grid-cols-1 sm:grid-cols-[1fr_140px_auto]'
                          : 'grid-cols-1'
                      )}
                    >
                      <CategoryPicker
                        id={index === 0 ? 'txn-category' : undefined}
                        value={line.category_id}
                        onChange={(categoryId) => setLine(line.key, { category_id: categoryId })}
                        groups={chart?.groups || []}
                        accountsById={accountsById}
                        accountTypes={chart?.account_types || []}
                        canCreate={canCreateCategory}
                        onCreate={createCategory}
                        excludeId={form.account_id}
                        invalid={!line.category_id}
                      />
                      {form.splitMode && (
                        <>
                          <Input
                            type="number"
                            step="0.01"
                            value={line.amount}
                            onChange={(event) => setLine(line.key, { amount: event.target.value })}
                            placeholder="0.00"
                            aria-label={`Amount for split line ${index + 1}`}
                            className={cn('h-10', adminInputClass)}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-10 w-9 text-[#062B1A]/45 hover:text-red-700"
                            aria-label={`Remove split line ${index + 1}`}
                            onClick={() => removeLine(line.key)}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </>
                      )}
                    </div>

                    {taxes.length > 0 && (
                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        <span className="text-[11px] text-[#062B1A]/45">Includes sales tax:</span>
                        {taxes.map((tax) => {
                          const on = line.tax_ids.includes(tax.id);
                          return (
                            <button
                              key={tax.id}
                              type="button"
                              onClick={() => toggleTax(line.key, tax.id)}
                              aria-pressed={on}
                              className={cn(
                                'rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors',
                                on
                                  ? 'border-[#062B1A] bg-[#062B1A] text-white'
                                  : 'border-[#062B1A]/15 text-[#062B1A]/55 hover:border-[#062B1A]/30'
                              )}
                            >
                              {tax.abbreviation || tax.name}
                            </button>
                          );
                        })}
                        <span className="text-[11px] text-[#062B1A]/35">
                          taken out of the amount, not added to it
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {form.splitMode && (
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-[#062B1A]/8 pt-2.5">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8"
                    onClick={addLine}
                  >
                    <Plus className="mr-1.5 size-3.5" />
                    Add another line
                  </Button>
                  <div className="text-right text-xs">
                    <p className="text-[#062B1A]/50">
                      {formatCents(balance.allocatedCents)} of {formatCents(amountCents)} allocated
                    </p>
                    {!balance.balanced && (
                      <p className="font-semibold text-red-700">
                        {balance.differenceCents > 0
                          ? `${formatCents(balance.differenceCents)} still to allocate`
                          : `${formatCents(Math.abs(balance.differenceCents))} over the amount`}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* ── Who ─────────────────────────────────────────────────── */}
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="txn-customer">
                  Customer <span className="text-[#062B1A]/35">(optional)</span>
                </Label>
                {locked ? (
                  <p className="mt-1 flex h-10 items-center rounded-md bg-[#F7F8FA] px-3 text-sm text-[#062B1A]">
                    {transaction.customer?.name || '—'}
                  </p>
                ) : (
                  <CustomerField
                    value={form.customer_ref}
                    valueLabel={customerLabel}
                    onChange={(ref, label) => {
                      setField({ customer_ref: ref });
                      setCustomerLabel(label);
                    }}
                  />
                )}
              </div>
              <div>
                <Label htmlFor="txn-payee">
                  Vendor or payee <span className="text-[#062B1A]/35">(optional)</span>
                </Label>
                {locked ? (
                  <p className="mt-1 flex h-10 items-center rounded-md bg-[#F7F8FA] px-3 text-sm text-[#062B1A]">
                    {transaction.payee?.name || '—'}
                  </p>
                ) : (
                  <select
                    id="txn-payee"
                    value={form.payee_ref}
                    onChange={(event) => setField({ payee_ref: event.target.value })}
                    className={cn(
                      'mt-1 h-10 w-full rounded-md border bg-white px-3 text-sm',
                      adminInputClass
                    )}
                  >
                    <option value="">No vendor</option>
                    {payees.map((payee) => (
                      <option key={payee.ref} value={payee.ref}>
                        {payee.name} ({payee.source_label})
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="txn-notes">Notes</Label>
              <Textarea
                id="txn-notes"
                rows={2}
                value={form.notes}
                onChange={(event) => setField({ notes: event.target.value })}
                placeholder="Anything the next person reading this row would want to know"
                className={cn('mt-1 resize-y', adminInputClass)}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={saving}
            className="bg-[#062B1A] text-white hover:bg-[#041B10]"
          >
            {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
            {editing ? 'Save changes' : 'Add transaction'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
